import React, { useState, useEffect, useMemo } from 'react';
import { backendAPI } from '@/utils/api';
import { 
  PlayIcon, 
  StopIcon, 
  PlusIcon, 
  ClockIcon,
  MagnifyingGlassIcon 
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

interface TimeEntry {
  id: string;
  description: string;
  project_id?: string;
  task_id?: string;
  start_time: string;
  end_time?: string;
  duration_hours: number;
  billable: boolean;
  status: 'running' | 'stopped' | 'completed';
  user_id: string;
  created_at: string;
}



const TimeTrackingPage: React.FC = () => {
  const [timeEntries, setTimeEntries] = useState<TimeEntry[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentTimer, setCurrentTimer] = useState<TimeEntry | null>(null);
  const [showStartTimer, setShowStartTimer] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [billableFilter, setBillableFilter] = useState('');

  const params = useMemo(() => {
    const p: Record<string, string> = {}
    if (search) p.search = search
    if (statusFilter) p.status = statusFilter
    if (billableFilter) p.billable = billableFilter
    p.page = '1'
    p.limit = '50'
    p.sort_by = 'start_time'
    p.sort_order = 'desc'
    return p
  }, [search, statusFilter, billableFilter])

  useEffect(() => {
    let mounted = true
    setIsLoading(true)
    backendAPI
      .getTimeEntries?.(params as any)
      .then(async (res: any) => {
        const rows = res?.data || []
        if (mounted) {
          setTimeEntries(rows)
          // Check for running timer
          const running = rows.find((entry: TimeEntry) => entry.status === 'running')
          setCurrentTimer(running || null)
        }
      })
      .catch(() => toast.error('Failed to load time entries'))
      .finally(() => mounted && setIsLoading(false))
    return () => {
      mounted = false
    }
  }, [params])

  const handleStartTimer = async (timerData: any) => {
    try {
      const response = await backendAPI.startTimer?.(timerData)
      if (response?.data) {
        setCurrentTimer(response.data)
        setShowStartTimer(false)
        toast.success('Timer started successfully!')
        // Refresh the list
        const res = await backendAPI.getTimeEntries?.(params as any)
        if (res?.data) setTimeEntries(res.data)
      }
    } catch (error) {
      toast.error('Failed to start timer')
    }
  }

  const handleStopTimer = async (entryId: string) => {
    try {
      await backendAPI.stopTimer?.(entryId)
      setCurrentTimer(null)
      toast.success('Timer stopped successfully!')
      // Refresh the list
      const res = await backendAPI.getTimeEntries?.(params as any)
      if (res?.data) setTimeEntries(res.data)
    } catch (error) {
      toast.error('Failed to stop timer')
    }
  }

  const formatDuration = (hours: number) => {
    const h = Math.floor(hours)
    const m = Math.round((hours - h) * 60)
    return `${h}h ${m}m`
  }

  const formatTime = (timeString: string) => {
    return new Date(timeString).toLocaleString()
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'running': return 'bg-green-100 text-green-800'
      case 'stopped': return 'bg-gray-100 text-gray-800'
      case 'completed': return 'bg-blue-100 text-blue-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const stats = {
    total: timeEntries.length,
    running: timeEntries.filter(e => e.status === 'running').length,
    billable: timeEntries.filter(e => e.billable).length,
    totalHours: timeEntries.reduce((sum, e) => sum + e.duration_hours, 0),
    billableHours: timeEntries.filter(e => e.billable).reduce((sum, e) => sum + e.duration_hours, 0)
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Time Tracking</h1>
        <p className="text-gray-600 mt-2">Track your time and manage billable hours</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        <div className="bg-white p-4 rounded-lg shadow">
          <p className="text-sm font-medium text-gray-600">Total Entries</p>
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <p className="text-sm font-medium text-gray-600">Running</p>
          <p className="text-2xl font-bold text-green-600">{stats.running}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <p className="text-sm font-medium text-gray-600">Billable</p>
          <p className="text-2xl font-bold text-blue-600">{stats.billable}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <p className="text-sm font-medium text-gray-600">Total Hours</p>
          <p className="text-2xl font-bold text-gray-900">{formatDuration(stats.totalHours)}</p>
        </div>
        <div className="bg-white p-4 rounded-lg shadow">
          <p className="text-sm font-medium text-gray-600">Billable Hours</p>
          <p className="text-2xl font-bold text-green-600">{formatDuration(stats.billableHours)}</p>
        </div>
      </div>

      {/* Current Timer */}
      {currentTimer && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ClockIcon className="h-6 w-6 text-green-600 animate-pulse" />
              <div>
                <h3 className="font-semibold text-green-800">Timer Running</h3>
                <p className="text-sm text-green-600">{currentTimer.description}</p>
              </div>
            </div>
            <button
              onClick={() => handleStopTimer(currentTimer.id)}
              className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
            >
              <StopIcon className="h-5 w-5" />
              Stop Timer
            </button>
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <div className="flex-1">
          <div className="relative">
            <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search time entries..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
        
        <div className="flex gap-2">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">All Status</option>
            <option value="running">Running</option>
            <option value="stopped">Stopped</option>
            <option value="completed">Completed</option>
          </select>
          
          <select
            value={billableFilter}
            onChange={(e) => setBillableFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">All Types</option>
            <option value="true">Billable</option>
            <option value="false">Non-billable</option>
          </select>
          
          <button
            onClick={() => setShowStartTimer(true)}
            disabled={!!currentTimer}
            className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
          >
            <PlayIcon className="h-5 w-5" />
            Start Timer
          </button>
          
          <button
            onClick={() => setShowManualEntry(true)}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <PlusIcon className="h-5 w-5" />
            Manual Entry
          </button>
        </div>
      </div>

      {/* Time Entries Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Start Time</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">End Time</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Billable</th>
              <th className="px-6 py-3" />
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {isLoading ? (
              <tr>
                <td className="px-6 py-10 text-center text-gray-500" colSpan={7}>Loading...</td>
              </tr>
            ) : timeEntries.length === 0 ? (
              <tr>
                <td className="px-6 py-10 text-center text-gray-500" colSpan={7}>No time entries found</td>
              </tr>
            ) : (
              timeEntries.map((entry) => (
                <tr key={entry.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm font-medium text-gray-900">{entry.description}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">{formatTime(entry.start_time)}</td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {entry.end_time ? formatTime(entry.end_time) : '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">{formatDuration(entry.duration_hours)}</td>
                  <td className="px-6 py-4 text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(entry.status)}`}>
                      {entry.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                      entry.billable ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}>
                      {entry.billable ? 'Billable' : 'Non-billable'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right text-sm">
                    {entry.status === 'running' && (
                      <button
                        onClick={() => handleStopTimer(entry.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        Stop
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Start Timer Modal */}
      {showStartTimer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Start Timer</h2>
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.currentTarget);
              handleStartTimer({
                description: formData.get('description') as string,
                project_id: formData.get('project_id') as string || undefined,
                task_id: formData.get('task_id') as string || undefined,
                billable: formData.get('billable') === 'true',
              });
            }}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <input
                    type="text"
                    name="description"
                    required
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="What are you working on?"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Project (Optional)</label>
                  <select
                    name="project_id"
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select Project</option>
                    <option value="1">Website Redesign</option>
                    <option value="2">Mobile App Development</option>
                    <option value="3">E-commerce Platform</option>
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Task (Optional)</label>
                  <select
                    name="task_id"
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select Task</option>
                    <option value="1">Design Review</option>
                    <option value="2">API Integration</option>
                    <option value="3">Testing</option>
                  </select>
                </div>
                
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="billable"
                      value="true"
                      defaultChecked
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Billable time</span>
                  </label>
                </div>
              </div>
              
              <div className="flex gap-2 mt-6">
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Start Timer
                </button>
                <button
                  type="button"
                  onClick={() => setShowStartTimer(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Manual Entry Modal */}
      {showManualEntry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-bold mb-4">Manual Time Entry</h2>
            <form onSubmit={(e) => {
              e.preventDefault();
              // Handle manual entry creation
              toast.success('Time entry created successfully!');
              setShowManualEntry(false);
            }}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <input
                    type="text"
                    name="description"
                    required
                    className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="What did you work on?"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Start Time</label>
                    <input
                      type="datetime-local"
                      name="start_time"
                      required
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700">End Time</label>
                    <input
                      type="datetime-local"
                      name="end_time"
                      required
                      className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      name="billable"
                      value="true"
                      defaultChecked
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="ml-2 text-sm text-gray-700">Billable time</span>
                  </label>
                </div>
              </div>
              
              <div className="flex gap-2 mt-6">
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Create Entry
                </button>
                <button
                  type="button"
                  onClick={() => setShowManualEntry(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TimeTrackingPage;
